"use client"
import { useState } from "react"
import { useLanguage } from "@/contexts/language-context"
import Image from "next/image"
import { demoOptions } from "@/lib/demo-data"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"

/**
 * DemoTabs: hero 下方标签式 demo 预览区域
 * 参考 simplify.jobs 标签风格，简化为水平滚动标签 + 下方预览
 */
export function DemoTabs() {
  const [activeId, setActiveId] = useState(demoOptions[0].id)
  const active = demoOptions.find(d => d.id === activeId)!
  const { t } = useLanguage()

  return (
    <section className="relative py-12 md:py-16 overflow-hidden bg-gradient-to-b from-white via-white to-white dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900" id="demo-tabs">
      {/* 顶部与 Hero 衔接的柔和过渡 */}
      <div className="pointer-events-none absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-white/70 via-white/90 to-white dark:from-zinc-950/60 dark:via-zinc-950 dark:to-zinc-950" />
      {/* 底部与 HeroShowcase 衔接的柔和过渡（下一节开始透明再到白色，因此这里做一个向下淡出） */}
      <div className="pointer-events-none absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-white dark:from-zinc-900 to-transparent" />
      <div className="container px-4 max-w-6xl mx-auto relative">
        <div className="flex justify-center gap-2 flex-wrap md:flex-nowrap overflow-x-auto md:overflow-visible pb-2 px-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-zinc-700">
          {demoOptions.map(opt => (
            <button
              key={opt.id}
              onClick={() => setActiveId(opt.id)}
              className={cn(
                "whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors select-none",
                "bg-gray-100 dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 hover:bg-gray-300 dark:hover:bg-gray-500",
                activeId === opt.id && "bg-gray-200 dark:bg-gray-600 text-white border-gray-300 dark:border-gray-500 shadow"
              )}
            >
              {
                t(`demo.shortTitle.${opt.id.replace(/-([a-z])/g, (_,c)=>c.toUpperCase())}` as any)
                || opt.shortTitle
                || opt.id
              }
            </button>
          ))}
        </div>
        <Card className="mt-6 shadow-lg border-0 bg-white/95 dark:bg-zinc-900/95">
          <CardContent className="p-0">
            <div className="relative overflow-hidden rounded-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/70 via-white to-indigo-50/60 dark:from-zinc-800 dark:via-zinc-900 dark:to-zinc-900" />
              <div className="relative grid md:grid-cols-2 gap-0 min-h-[400px]">
                {/* 左侧：输入 + 骨架 */}
                <div className="relative p-6 md:p-8 flex flex-col border-b md:border-b-0 md:border-r border-gray-200/60 dark:border-zinc-700/60">
                  <div className="mb-4">
                    <span className="inline-block px-2 py-1 text-[11px] rounded-md bg-blue-100 text-blue-700 dark:bg-indigo-800/60 dark:text-indigo-200 font-medium tracking-wide">{t('demoTabs.inputPrompt') || 'INPUT PROMPT'}</span>
                  </div>
                  <div className="relative flex-1 mb-6 rounded-lg bg-white/70 dark:bg-zinc-800/60 border border-gray-200 dark:border-zinc-700 backdrop-blur-sm shadow-sm">
                    <div className="p-4 h-full overflow-hidden">
                      <div className="text-xs font-mono text-gray-500 uppercase tracking-wide mb-2">{t('demoTabs.context') || 'CONTEXT'}</div>
                      {/* 交错：文本 + 骨架 */}
                      <div className="space-y-2 text-[13px] leading-snug">
                        {[
                          { type: 'text', content: `${active.inputContent.split('\n')[0]}` },
                          { type: 'skeleton', w: 78 },
                          { type: 'skeleton', w: 72 },
                          { type: 'skeleton', w: 60 },
                          { type: 'skeleton', w: 50 },
                        ].map((row, i) => row.type === 'text' ? (
                          <p key={i} className="text-gray-700 dark:text-gray-300 font-medium">{row.content}</p>
                        ) : (
                          <div key={i} className="h-3 rounded bg-gray-200/70 dark:bg-zinc-700/60 animate-pulse" style={{ width: `${row.w}%` }} />
                        ))}
                      </div>
                      <div className="mt-4 text-xs font-mono text-gray-500 uppercase tracking-wide mb-2">{t('demoTabs.description') || 'Description'}</div>
                      <div className="space-y-2">
                        {[
                          { type: 'text', content: `${active.description.split('\n')[0]}` },
                          { type: 'skeleton', w: 86 },
                          { type: 'skeleton', w: 70 },
                          { type: 'skeleton', w: 60 },
                        ].map((row, i) => row.type === 'text' ? (
                          <p key={i} className="text-gray-700 dark:text-gray-300 text-[13px]">{row.content}</p>
                        ) : (
                          <div key={i} className="h-3 rounded bg-gray-200/70 dark:bg-zinc-700/60 animate-pulse" style={{ width: `${row.w}%` }} />
                        ))}
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white dark:from-zinc-800 to-transparent pointer-events-none" />
                    </div>
                    <div className="absolute top-2 right-2 text-[10px] text-gray-400 font-mono">{t('demoTabs.pathPrefix') || 'demo'}/{active.id}</div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 h-10 rounded-md bg-gray-200/60 dark:bg-zinc-700/60 animate-pulse" />
                    <div className="w-24 h-10 rounded-md bg-blue-600/30 text-white flex items-center justify-center text-xs font-medium shadow">{t('demoTabs.generate') || 'Generate'}</div>
                  </div>
                </div>
                {/* 右侧：输出 + 实际示例文本 + 骨架 */}
                <div className="relative p-6 md:p-8 flex flex-col">
                  <div className="mb-4 flex items-center gap-2">
                    <span className="inline-block px-2 py-1 text-[11px] rounded-md bg-green-100 text-green-700 dark:bg-emerald-800/60 dark:text-emerald-200 font-medium tracking-wide">{t('demoTabs.aiOutput') || 'AI OUTPUT'}</span>
                    <span className="text-[10px] text-gray-400">({t('demoTabs.sample') || 'sample'})</span>
                  </div>
                  <div className="relative flex-1 rounded-lg border border-gray-200 dark:border-zinc-700 bg-gradient-to-br from-white/90 to-white/40 dark:from-zinc-800/70 dark:to-zinc-800/30 backdrop-blur-sm shadow-sm overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-300/10 dark:bg-indigo-500/10 blur-2xl rounded-full" />
                      <div className="absolute bottom-0 right-0 w-56 h-56 bg-indigo-300/10 dark:bg-blue-500/10 blur-2xl rounded-full" />
                    </div>
                    <div className="relative p-5 h-full flex flex-col gap-4 overflow-hidden">
                      <div className="space-y-3 text-sm leading-relaxed text-gray-800 dark:text-gray-200 font-medium">
                        {active.sampleOutput?.split('\n').slice(0,3).map((line, idx) => (
                          <p key={idx}>{line}</p>
                        ))}
                      </div>
                      <div className="mt-2 space-y-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="h-3 rounded bg-gray-200/70 dark:bg-zinc-700/60 animate-pulse" style={{ width: `${95 - i * 12}%` }} />
                        ))}
                      </div>
                      <div className="mt-auto flex flex-wrap gap-2 pt-2">
                        <span className="text-[10px] px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-indigo-700/60 dark:text-indigo-200">{active.scenario}</span>
                        <span className="text-[10px] px-2 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-zinc-700/60 dark:text-zinc-300">{active.target}</span>
                        <span className="text-[10px] px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-emerald-700/60 dark:text-emerald-200">{active.tone}</span>
                      </div>
                      <div className="absolute top-2 right-3 text-[10px] text-gray-400 font-mono">{t('demoTabs.response') || 'response'}</div>
                      <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white dark:from-zinc-800 to-transparent pointer-events-none" />
                    </div>
                  </div>
                    <div className="mt-4 flex items-center gap-3 text-[11px] text-gray-400">
                    <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                    <span>{t('demoTabs.streaming') || 'Streaming tokens...'}</span>
                  </div>
                </div>
              </div>
              {/* 角落大圆角遮罩，模拟整体一张预览图感受 */}
              <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-black/5 dark:ring-white/5" />
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  )
}
